import { Injectable } from '@angular/core';
import {
  LocalStorage,
  ManagementService,
} from '@principle-theorem/ng-principle-shared';
import { snapshot } from '@principle-theorem/shared';
import { BehaviorSubject } from 'rxjs';

const SIDEBAR_STORAGE_KEY = 'managementToolSidebarOpened';

interface ISidebarState {
  opened: boolean;
  showDevTools: boolean;
  obfuscateNames: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ManagementToolSidebarService {
  private _storage = new LocalStorage<ISidebarState>(SIDEBAR_STORAGE_KEY);
  opened$ = new BehaviorSubject<boolean>(this._storage.value?.opened ?? false);
  showDevTools$ = new BehaviorSubject<boolean>(
    this._storage.value?.showDevTools ?? false
  );
  obfuscateNames$ = new BehaviorSubject<boolean>(
    this._storage.value?.obfuscateNames ?? false
  );

  constructor(private _management: ManagementService) {}

  async toggle(): Promise<void> {
    if (this.opened$.value) {
      this.close();
    } else {
      await this.open();
    }
  }

  async open(): Promise<void> {
    const managementUser = await snapshot(this._management.user$);
    if (!managementUser) {
      return;
    }
    this.opened$.next(true);
    this._updateStorage({ opened: true });
  }

  close(): void {
    this.opened$.next(false);
    this._updateStorage({ opened: false });
  }

  toggleDevTools(): void {
    const next = !this.showDevTools$.value;
    this.showDevTools$.next(next);
    this._updateStorage({ showDevTools: next });
  }

  toggleObfuscateNames(): void {
    const next = !this.obfuscateNames$.value;
    this.obfuscateNames$.next(next);
    this._updateStorage({ obfuscateNames: next });
  }

  private _updateStorage(partial: Partial<ISidebarState>): void {
    this._storage.update({
      opened: this.opened$.value,
      showDevTools: this.showDevTools$.value,
      obfuscateNames: this.obfuscateNames$.value,
      ...partial,
    });
  }
}
